/**
 * Checking one document and booking it.
 *
 * The original is on the right and the extracted values on the left, because confirming a number
 * means looking at the paper. Every field that was read automatically carries a mark saying where
 * it came from, so nobody has to trust an amount blindly. Booking is deliberate and one-way-ish:
 * a booked document can no longer be deleted (§ 132 BAO), only archived.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  BookCheck,
  Lock,
  Package,
  RefreshCw,
  Trash2,
  TriangleAlert,
  Undo2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import {
  BelegNumber,
  KindBadge,
  SourceMark,
  StatusLabel,
} from "@/components/documents/document-badges";
import { AssetDialog } from "@/components/assets/asset-dialog";
import { CropEditor } from "@/components/documents/crop-editor";
import { DocumentOrigin } from "@/components/documents/document-origin";
import { DocumentPreview } from "@/components/documents/document-preview";
import { InvoiceChecklist } from "@/components/documents/invoice-checklist";
import { VatLinesField } from "@/components/documents/vat-lines-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useCategories,
  useDeleteDocument,
  useDocument,
  useUpdateDocument,
} from "@/hooks/use-documents";
import { type DocumentRecord } from "@/lib/api/documents";
import { ownNames, rereadDocument } from "@/lib/api/extract";
import { isPlausibleEuVatId, normalizeVatId } from "@/lib/at/uid";
import { retentionUntil } from "@/lib/at/periods";
import {
  linesForDocument,
  printedTotalGap,
  splitGross,
  TAX_TREATMENTS,
  totalsFromLines,
  type VatLine,
} from "@/lib/at/vat";
import { track } from "@/lib/platform/analytics";
import { applyServerErrors } from "@/lib/utils/form-errors";
import { formatDate, formatMoney, parseDecimal } from "@/lib/utils/format";
import { useWorkspace } from "@/lib/session/workspace-context";
import { useAssets } from "@/hooks/use-assets";

const PAYMENT_STATUSES = ["open", "paid", "partial", "not_applicable"] as const;
const PAYMENT_METHODS = [
  "cash",
  "card",
  "bank_transfer",
  "direct_debit",
  "paypal",
  "other",
] as const;

/** PocketBase returns dates as "2026-03-14 00:00:00.000Z"; <input type="date"> wants the day. */
const day = (value: string | undefined) => (value ? value.slice(0, 10) : "");

export function DocumentDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workspace, canWrite } = useWorkspace();
  const { data: assets } = useAssets(workspace?.id);
  const [assetOpen, setAssetOpen] = useState(false);

  const { data: document, isPending } = useDocument(id);
  const { data: categories } = useCategories(workspace?.id);
  const updateDocument = useUpdateDocument();
  const deleteDocument = useDeleteDocument();

  const [vatLines, setVatLines] = useState<VatLine[]>([]);
  const [confirmBook, setConfirmBook] = useState(false);
  const [reExtracting, setReExtracting] = useState(false);

  const schema = z.object({
    invoiceNumber: z.string().max(80),
    partnerName: z.string().max(200),
    partnerVatId: z
      .string()
      .refine((value) => value === "" || isPlausibleEuVatId(value), t("validation.vatId")),
    partnerAddress: z.string().max(300),
    partnerIban: z.string().max(40),
    issueDate: z.string(),
    serviceDate: z.string(),
    dueDate: z.string(),
    paymentStatus: z.enum(PAYMENT_STATUSES),
    paymentMethod: z.enum(PAYMENT_METHODS),
    paidDate: z.string(),
    taxTreatment: z.enum(TAX_TREATMENTS),
    category: z.string(),
    deductiblePercent: z.number().min(0).max(100),
    notes: z.string().max(2000),
    tip: z.number().min(0),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      invoiceNumber: "",
      partnerName: "",
      partnerVatId: "",
      partnerAddress: "",
      partnerIban: "",
      issueDate: "",
      serviceDate: "",
      dueDate: "",
      paymentStatus: "open",
      paymentMethod: "bank_transfer",
      paidDate: "",
      taxTreatment: "standard",
      category: "",
      deductiblePercent: 100,
      notes: "",
      tip: 0,
    },
  });

  const { reset } = form;
  useEffect(() => {
    if (!document) return;
    reset({
      invoiceNumber: document.invoiceNumber ?? "",
      partnerName: document.partnerName ?? "",
      partnerVatId: document.partnerVatId ?? "",
      partnerAddress: document.partnerAddress ?? "",
      partnerIban: document.partnerIban ?? "",
      issueDate: day(document.issueDate),
      serviceDate: day(document.serviceDate),
      dueDate: day(document.dueDate),
      paymentStatus: document.paymentStatus || "open",
      paymentMethod: document.paymentMethod || "bank_transfer",
      paidDate: day(document.paidDate),
      taxTreatment: document.taxTreatment || "standard",
      category: document.category ?? "",
      deductiblePercent: document.deductiblePercent ?? 100,
      notes: document.notes ?? "",
      tip: document.tip ?? 0,
    });
    setVatLines(linesForDocument(document));
  }, [document, reset]);

  if (isPending) return <Skeleton className="h-96 w-full" aria-busy="true" />;
  if (!document) {
    return (
      <div className="space-y-4">
        <p>{t("document.missing")}</p>
        <Button asChild variant="outline">
          <Link to="/documents">
            <ArrowLeft /> {t("document.back")}
          </Link>
        </Button>
      </div>
    );
  }

  // Without lines (an amount whose rate is unknown) the stored totals stand: showing or saving 0
  // would silently lose the amount.
  const totals =
    vatLines.length > 0
      ? totalsFromLines(vatLines)
      : {
          netTotal: document.netTotal ?? 0,
          vatTotal: document.vatTotal ?? 0,
          grossTotal: document.grossTotal ?? 0,
        };
  // A booked document keeps what it was booked with (server/pb_hooks/bookkeeping.pb.js); payment,
  // due date and notes stay open, and undoing the booking is how it is corrected.
  const booked = document.status === "booked";
  const locked = !canWrite || booked;
  const sources = document.extracted?.sources ?? {};
  const warnings = document.extracted?.warnings ?? [];
  const keepUntil = retentionUntil(day(document.issueDate) || day(document.created));

  async function save(values: Values, status?: DocumentRecord["status"]) {
    if (!document) return;
    const open = {
      paymentStatus: values.paymentStatus,
      paymentMethod: values.paymentMethod,
      paidDate: values.paidDate,
      dueDate: values.dueDate,
      notes: values.notes,
    };
    try {
      await updateDocument.mutateAsync({
        id: document.id,
        input: {
          ...(booked
            ? open
            : {
                ...values,
                partnerVatId: normalizeVatId(values.partnerVatId),
                vatLines,
                netTotal: totals.netTotal,
                vatTotal: totals.vatTotal,
                grossTotal: totals.grossTotal,
              }),
          ...(status ? { status } : {}),
        },
      });
      if (status === "booked") track("document_booked", { kind: document.kind });
      toast.success(
        status === "booked"
          ? t("document.booked")
          : status === "review" && booked
            ? t("document.unbooked")
            : t("document.saved"),
      );
    } catch (error) {
      const message = applyServerErrors(error, (name, err) =>
        form.setError(name as keyof Values, err),
      );
      if (message) toast.error(message);
    }
  }

  /** Re-reads the stored file. Useful when a first attempt failed or a key was added later. */
  async function reExtract() {
    if (!document) return;
    setReExtracting(true);
    try {
      const { patch } = await rereadDocument(document, {
        kind: document.kind,
        ownVatId: workspace?.vatId,
        ownIban: workspace?.iban,
        ownNames: ownNames(workspace),
      });
      await updateDocument.mutateAsync({ id: document.id, input: patch });
      await queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast.success(t("document.reExtracted"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("document.reExtractFailed"));
    } finally {
      setReExtracting(false);
    }
  }

  const bookable = Boolean(form.watch("issueDate")) && totals.grossTotal > 0;
  // The VAT lines against the total printed on the receipt (what the card was charged).
  const printedGross = document.extracted?.fields?.grossTotal;
  const totalGap = printedTotalGap(vatLines, printedGross);
  // The extraction's complaints about its own arithmetic are stale once the lines add up to the
  // printed total (QA-0048); the gap alert above the buttons covers the live state.
  const shownWarnings =
    typeof printedGross === "number" && totalGap === 0
      ? warnings.filter((w) => w !== "vat_lines_mismatch" && w !== "totals_inconsistent")
      : warnings;
  const book = form.handleSubmit((values) => save(values, "booked"));
  const linkedAsset = assets?.find((asset) => asset.document === document.id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="icon" aria-label={t("document.back")}>
          <Link to="/documents">
            <ArrowLeft />
          </Link>
        </Button>
        <KindBadge kind={document.kind} />
        <BelegNumber value={document.docNumber} />
        <h1 className="min-w-0 text-xl font-semibold tracking-tight wrap-anywhere">
          {document.partnerName || t("document.noPartner")}
        </h1>
        <StatusLabel status={document.status} />

        <div className="ml-auto flex flex-wrap gap-2">
          {document.kind !== "AR" && linkedAsset && (
            <Button asChild variant="ghost" size="sm" data-testid="asset-link">
              <Link to="/assets">
                <Package /> {t("assets.inRegister")}
              </Link>
            </Button>
          )}
          {document.kind !== "AR" && !linkedAsset && canWrite && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAssetOpen(true)}
              data-testid="make-asset"
            >
              <Package /> {t("assets.fromDocument")}
            </Button>
          )}
          {canWrite && !booked && (
            <CropEditor
              document={document}
              hints={{
                ownVatId: workspace?.vatId,
                ownIban: workspace?.iban,
                ownNames: ownNames(workspace),
              }}
            />
          )}
          {canWrite && !booked && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void reExtract()}
              disabled={reExtracting}
              data-testid="re-extract"
            >
              <RefreshCw className={reExtracting ? "animate-spin" : ""} /> {t("document.reExtract")}
            </Button>
          )}
          {canWrite && document.status !== "booked" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="delete-document">
                  <Trash2 /> {t("common.delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("document.deleteTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("document.deleteHint")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={async () => {
                      await deleteDocument.mutateAsync(document.id);
                      toast.success(t("document.deleted"));
                      navigate("/documents");
                    }}
                  >
                    {t("common.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {document.ocrStatus === "failed" && (
        <Alert variant="destructive" data-testid="ocr-failed">
          <TriangleAlert className="size-4" />
          <AlertTitle>{t("document.ocrFailedTitle")}</AlertTitle>
          <AlertDescription>{t("document.ocrFailedText")}</AlertDescription>
        </Alert>
      )}

      {document.extractionConfidence > 0 && document.status !== "booked" && (
        <Alert data-testid="extraction-summary">
          <AlertTitle>
            {t("extraction.summary", {
              percent: Math.round((document.extractionConfidence ?? 0) * 100),
            })}
          </AlertTitle>
          <AlertDescription>
            <span>{t("extraction.checkHint")}</span>
            {shownWarnings.length > 0 && (
              <ul className="mt-1 list-disc pl-4">
                {shownWarnings.map((warning) => (
                  <li key={warning}>
                    {t(`extraction.warnings.${warning}`, t("extraction.warnings.generic"))}
                  </li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      )}

      {booked && canWrite && (
        <p
          className="flex items-start gap-2 text-sm text-muted-foreground"
          data-testid="booked-locked"
        >
          <Lock className="mt-0.5 size-4 shrink-0" />
          {t("document.lockedHint")}
        </p>
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
        <Form {...form}>
          <form
            className="space-y-8"
            onSubmit={form.handleSubmit((values) => save(values))}
            data-testid="document-form"
          >
            <section className="space-y-4">
              <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
                {document.kind === "AR" ? t("document.customer") : t("document.supplier")}
              </h2>
              <FormField
                control={form.control}
                name="partnerName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      {t("document.partnerName")} <SourceMark source={sources.partnerName} />
                    </FormLabel>
                    <FormControl>
                      <Input data-testid="partner-name" disabled={locked} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="partnerVatId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        {t("document.partnerVatId")} <SourceMark source={sources.partnerVatId} />
                      </FormLabel>
                      <FormControl>
                        <Input
                          data-testid="partner-vat-id"
                          className="num"
                          placeholder="ATU…"
                          disabled={locked}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="partnerIban"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        {t("document.partnerIban")} <SourceMark source={sources.partnerIban} />
                      </FormLabel>
                      <FormControl>
                        <Input className="num" disabled={locked} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="partnerAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      {t("document.partnerAddress")} <SourceMark source={sources.partnerAddress} />
                    </FormLabel>
                    <FormControl>
                      <Input disabled={locked} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>

            <section className="space-y-4">
              <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
                {t("document.invoiceSection")}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="invoiceNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        {t("document.invoiceNumber")} <SourceMark source={sources.invoiceNumber} />
                      </FormLabel>
                      <FormControl>
                        <Input
                          data-testid="invoice-number"
                          className="num"
                          disabled={locked}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="issueDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        {t("document.issueDate")} <SourceMark source={sources.issueDate} />
                      </FormLabel>
                      <FormControl>
                        <Input
                          data-testid="issue-date"
                          type="date"
                          className="num"
                          disabled={locked}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="serviceDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        {t("document.serviceDate")} <SourceMark source={sources.serviceDate} />
                      </FormLabel>
                      <FormControl>
                        <Input type="date" className="num" disabled={locked} {...field} />
                      </FormControl>
                      <FormDescription>{t("document.serviceDateHint")}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="dueDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        {t("document.dueDate")} <SourceMark source={sources.dueDate} />
                      </FormLabel>
                      <FormControl>
                        <Input type="date" className="num" disabled={!canWrite} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
                {t("document.amountsSection")}
              </h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="quick-gross">{t("document.quickGross")}</Label>
                  {/* One gross amount cannot say how it divides across several rates (QA-0051):
                      with a mixed split the lines below are the only way to change it. */}
                  <Input
                    id="quick-gross"
                    inputMode="decimal"
                    className="text-right num"
                    disabled={locked || vatLines.length > 1}
                    placeholder={t("document.quickGrossPlaceholder")}
                    data-testid="quick-gross"
                    onBlur={(event) => {
                      const gross = parseDecimal(event.target.value);
                      if (gross === undefined || gross <= 0) return;
                      const rate = vatLines[0]?.rate ?? 20;
                      setVatLines([splitGross(gross, rate)]);
                      event.target.value = "";
                    }}
                  />
                  <p className="text-xs text-muted-foreground" data-testid="quick-gross-hint">
                    {vatLines.length > 1
                      ? t("document.quickGrossMixed")
                      : t("document.quickGrossHint")}
                  </p>
                </div>
                <FormField
                  control={form.control}
                  name="taxTreatment"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("document.taxTreatment")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} disabled={locked}>
                        <FormControl>
                          <SelectTrigger data-testid="tax-treatment">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TAX_TREATMENTS.map((treatment) => (
                            <SelectItem key={treatment} value={treatment}>
                              {t(`taxTreatment.${treatment}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {/* A Kleinunternehmer owes reverse-charge and acquisition VAT, deducts none. */}
                        {workspace?.vatScheme === "small_business" &&
                        document.kind !== "AR" &&
                        (form.watch("taxTreatment") === "reverse_charge" ||
                          form.watch("taxTreatment") === "eu_intra_community")
                          ? t("taxTreatmentHintSmallBusiness")
                          : t(`taxTreatmentHint.${form.watch("taxTreatment")}`)}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <VatLinesField
                value={vatLines}
                onChange={setVatLines}
                totals={totals}
                currency={document.currency || "EUR"}
                disabled={locked}
              />

              {document.kind !== "AR" && (
                <FormField
                  control={form.control}
                  name="tip"
                  render={({ field }) => (
                    <FormItem className="max-w-xs">
                      <FormLabel>{t("document.tip")}</FormLabel>
                      <FormControl>
                        <Input
                          // Uncontrolled so "6," can be typed; re-created when the record changes.
                          key={`${document.id}-${document.tip ?? 0}`}
                          inputMode="decimal"
                          className="text-right num"
                          disabled={locked}
                          name={field.name}
                          ref={field.ref}
                          defaultValue={field.value ? String(field.value).replace(".", ",") : ""}
                          placeholder="0,00"
                          data-testid="tip"
                          onBlur={(event) => {
                            field.onChange(parseDecimal(event.target.value) ?? 0);
                            field.onBlur();
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        {form.watch("tip") > 0
                          ? t("document.tipCharged", {
                              amount: formatMoney(
                                totals.grossTotal + form.watch("tip"),
                                document.currency,
                              ),
                            })
                          : t("document.tipHint")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </section>

            <section className="space-y-4">
              <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
                {t("document.bookingSection")}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("document.category")}</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          const category = categories?.find((entry) => entry.id === value);
                          if (category?.deductiblePercent !== undefined) {
                            form.setValue("deductiblePercent", category.deductiblePercent);
                          }
                        }}
                        value={field.value}
                        disabled={locked}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="category">
                            <SelectValue placeholder={t("document.chooseCategory")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(categories ?? [])
                            .filter((category) =>
                              document.kind === "AR"
                                ? category.kind === "revenue"
                                : category.kind === "expense",
                            )
                            .map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                <span className="mr-2 ledger-id">{category.accountNumber}</span>
                                {category.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="deductiblePercent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("document.deductiblePercent")}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          className="num"
                          disabled={locked}
                          name={field.name}
                          ref={field.ref}
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={(event) => field.onChange(event.target.valueAsNumber || 0)}
                        />
                      </FormControl>
                      <FormDescription>{t("document.deductibleHint")}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="paymentStatus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("document.paymentStatus")}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={!canWrite}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="payment-status">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PAYMENT_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {t(`paymentStatus.${status}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="paymentMethod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        {t("document.paymentMethod")} <SourceMark source={sources.paymentMethod} />
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={!canWrite}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="payment-method">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PAYMENT_METHODS.map((method) => (
                            <SelectItem key={method} value={method}>
                              {t(`paymentMethod.${method}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="paidDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("document.paidDate")}</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          className="num"
                          disabled={!canWrite}
                          data-testid="paid-date"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>{t("document.paidDateHint")}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("document.notes")}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} disabled={!canWrite} {...field} />
                    </FormControl>
                    <FormDescription>{t("document.notesHint")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>

            {canWrite && document.status !== "booked" && totalGap !== 0 && (
              <Alert variant="destructive" data-testid="total-gap">
                <AlertDescription>
                  {t("document.totalGap", {
                    lines: formatMoney(totals.grossTotal),
                    printed: formatMoney(Number(printedGross)),
                  })}
                </AlertDescription>
              </Alert>
            )}

            {canWrite && (
              <div className="flex flex-wrap gap-2 border-t pt-4">
                <Button
                  type="submit"
                  variant="outline"
                  disabled={updateDocument.isPending}
                  data-testid="save-document"
                >
                  {t("common.save")}
                </Button>
                {document.status !== "booked" && (
                  <Button
                    type="button"
                    disabled={!bookable || updateDocument.isPending}
                    data-testid="book-document"
                    onClick={totalGap !== 0 ? () => setConfirmBook(true) : book}
                  >
                    <BookCheck /> {t("document.book")}
                  </Button>
                )}
                <AlertDialog open={confirmBook} onOpenChange={setConfirmBook}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("document.totalGapConfirmTitle")}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("document.totalGapConfirmText", {
                          lines: formatMoney(totals.grossTotal),
                          printed: formatMoney(Number(printedGross)),
                        })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction data-testid="book-anyway" onClick={() => void book()}>
                        {t("document.totalGapConfirm", { lines: formatMoney(totals.grossTotal) })}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {booked && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="outline" data-testid="unbook-document">
                        <Undo2 /> {t("document.unbook")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("document.unbookTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("document.unbookText")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                          data-testid="unbook-confirm"
                          onClick={form.handleSubmit((values) => save(values, "review"))}
                        >
                          {t("document.unbook")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {booked && (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="archive-document"
                    onClick={form.handleSubmit((values) => save(values, "archived"))}
                  >
                    {t("document.archive")}
                  </Button>
                )}
                {!bookable && (
                  <p className="self-center text-xs text-muted-foreground">
                    {t("document.bookableHint")}
                  </p>
                )}
              </div>
            )}
          </form>
        </Form>

        <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start">
          <DocumentPreview document={document} />
          <DocumentOrigin document={document} />
          <div className="rounded-lg border p-4">
            <InvoiceChecklist
              document={{
                kind: document.kind,
                invoiceNumber: form.watch("invoiceNumber"),
                partnerName: form.watch("partnerName"),
                partnerAddress: form.watch("partnerAddress"),
                partnerVatId: form.watch("partnerVatId"),
                issueDate: form.watch("issueDate"),
                serviceDate: form.watch("serviceDate"),
                netTotal: totals.netTotal,
                vatTotal: totals.vatTotal,
                grossTotal: totals.grossTotal,
                vatLines,
                taxTreatment: form.watch("taxTreatment"),
              }}
              workspace={workspace ?? {}}
            />
          </div>
          <dl className="space-y-2 rounded-lg border p-4 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("document.grossTotal")}</dt>
              <dd className="font-medium num">
                {formatMoney(totals.grossTotal, document.currency)}
              </dd>
            </div>
            {keepUntil && (
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{t("document.keepUntil")}</dt>
                <dd className="num">{formatDate(keepUntil)}</dd>
              </div>
            )}
            {document.qrPayload && (
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{t("document.scannedCode")}</dt>
                <dd className="text-success" data-testid="scanned-code">
                  {t(`extraction.code.${document.extracted?.code?.type ?? "unknown"}`)}
                </dd>
              </div>
            )}
          </dl>
        </aside>
      </div>
      <AssetDialog
        open={assetOpen}
        onOpenChange={setAssetOpen}
        prefill={{
          name: document.partnerName,
          inServiceDate: document.issueDate,
          // Net when the input tax is deducted; a Kleinunternehmer's cost is the gross amount.
          cost: workspace?.vatScheme === "small_business" ? document.grossTotal : document.netTotal,
          document: document.id,
        }}
      />
    </div>
  );
}
